import { Box, Button, CircularProgress, Text } from '@streamelements/frontend-ui';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AllowedProvider, platformPrettyName } from '../../constants/common';
import { providerIcons } from '../ProviderIcon';
import { requestLocalNetworkAccess } from '../../utils/localNetworkAccess';

type Props = {
  port: number;
  authUrl: string;
  provider: AllowedProvider;
};

const containerStyles = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  flexDirection: 'column',
  gap: '1rem',
  height: '100vh',
  textAlign: 'center',
  padding: '2rem',
} as const;

const iconRowStyles = {
  display: 'flex',
  alignItems: 'center',
  gap: '0.75rem',
} as const;

export function PreAuthPermissionGate({ port, authUrl, provider }: Props) {
  const queryClient = useQueryClient();
  const Icon = providerIcons[provider];
  const platformName = platformPrettyName[provider] || provider;

  const { data: permissionResult, isFetching } = useQuery({
    queryKey: ['lna-permission', port],
    queryFn: async () => {
      const result = await requestLocalNetworkAccess(port);
      if (result.granted) {
        window.location.href = authUrl;
      }
      return result;
    },
    retry: false,
    refetchOnWindowFocus: false,
  });

  const handleRetry = () => {
    queryClient.refetchQueries({ queryKey: ['lna-permission', port] });
  };

  // Show checking state while fetching or before first result
  if (isFetching || !permissionResult) {
    return (
      <Box css={containerStyles}>
        <Box css={iconRowStyles}>
          <Box as="img" src="/logo.png" css={{ width: '3.25rem' }} />
          <CircularProgress.Root size={24} variant="indeterminate" value={25} />
          <Icon size="xl" css={{ '&&': { fontSize: '2.5rem' } }} />
        </Box>
        <Text.Heading level={5} css={{ m: 0 }} weight="bold">
          Preparing connection...
        </Text.Heading>
        <Text.Body css={{ color: '$textPrimary70' }}>
          Setting up secure connection to SE.Live
        </Text.Body>
      </Box>
    );
  }

  // Show granted state (brief moment before redirect)
  if (permissionResult?.granted) {
    return (
      <Box css={containerStyles}>
        <Box css={iconRowStyles}>
          <Box as="img" src="/logo.png" css={{ width: '3.25rem' }} />
          <Box as="img" src="/handshake.png" css={{ width: '1.5rem' }} />
          <Icon size="xl" css={{ '&&': { fontSize: '2.5rem' } }} />
        </Box>
        <Text.Heading level={5} css={{ m: 0 }} weight="bold">
          Redirecting to {platformName}...
        </Text.Heading>
        <Text.Body css={{ color: '$textPrimary70' }}>
          Please wait while we redirect you
        </Text.Body>
      </Box>
    );
  }

  // Permission explicitly denied - show instructions to reset
  if (permissionResult?.permissionState === 'denied') {
    return (
      <Box css={containerStyles}>
        <Box css={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <Box as="img" src="/logo.png" css={{ width: '3.25rem' }} />
          <Box as="img" src="/cross-mark.png" css={{ width: '1.5rem' }} />
          <Icon size="xl" css={{ '&&': { fontSize: '2.5rem' } }} />
        </Box>
        <Text.Heading level={5} css={{ m: 0 }} weight="bold">
          Permission Blocked
        </Text.Heading>
        <Text.Body css={{ color: '$textPrimary70', maxWidth: '45ch' }}>
          Local network access was denied. To continue, please reset the permission
          in your browser's site settings, then click the button below.
        </Text.Body>
        <Text.Body variant="caption" css={{ color: '$textPrimary50', maxWidth: '45ch' }}>
          Look for an icon near the address bar (usually a lock, tune, or info icon)
          to access site permissions. Reset "Local network access" to "Ask" or "Allow".
        </Text.Body>
        <Button
          color="primary"
          onClick={handleRetry}
          css={{ mt: '1rem' }}
        >
          Try Again
        </Button>
      </Box>
    );
  }

  // Permission prompt state - initial request
  return (
    <Box css={containerStyles}>
      <Box css={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
        <Box as="img" src="/logo.png" css={{ width: '3.25rem' }} />
        <Box as="img" src="/handshake.png" css={{ width: '1.5rem' }} />
        <Icon size="xl" css={{ '&&': { fontSize: '2.5rem' } }} />
      </Box>
      <Text.Heading level={5} css={{ m: 0 }} weight="bold">
        Local Network Access Required
      </Text.Heading>
      <Text.Body css={{ color: '$textPrimary70', maxWidth: '40ch' }}>
        SE.Live needs permission to communicate with the desktop app.
        When prompted, please click <strong>"Allow"</strong> to continue.
      </Text.Body>
      <Button
        color="primary"
        onClick={handleRetry}
        css={{ mt: '0.5rem' }}
      >
        Grant Access
      </Button>
      <Text.Body variant="caption" css={{ color: '$textPrimary50', maxWidth: '50ch', mt: '1rem' }}>
        This permission allows the browser to authenticate you on the SE.Live
        application running on your computer.{' '}
        <Box
          as="a"
          href="https://developer.chrome.com/blog/local-network-access"
          target="_blank"
          rel="noopener noreferrer"
          css={{ color: '$uiPrimaryMain', textDecoration: 'underline' }}
        >
          Learn more
        </Box>
      </Text.Body>
    </Box>
  );
}
